import { memo, useEffect } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { CircularProgress, Divider, Stack } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ReactFlowProvider } from '@xyflow/react'
import { DocumentIdOverlay } from '@/hooks/useDocumentIdOverlay'
import useSessionStorage from '@/hooks/useSessionStorage'
import useValidationNavList from '@/hooks/useValidationNavList'
import useGetValidationDocDataQueries from '@/services/hooks/useGetValidationDocDataQueries'
import { Dialog } from '@/components/dialog'
import DocumentImagePageProvider from '@/components/image-zoom-pan/providers/DocumentImagePageProvider'
import DocumentProvider from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import QueryKeysMapProvider from '@/components/providers/QueryKeyProvider'
import SizeProvider from '@/components/size-provider/SizeProvider'
import DocumentChipsProvider from '@/components/validation/providers/DocumentChipsProvider'
import DocumentRowsProvider from '@/components/validation/providers/DocumentRowsProvider'
import DocumentRowValuesProvider from '@/components/validation/providers/DocumentRowValuesProvider'
import DocumentsChangeSetsProvider from '@/components/validation/providers/DocumentsChangeSetsProvider'
import FieldErrorProvider from '@/components/validation/providers/FieldErrorProvider'
import HoveredTableFieldProvider from '@/components/validation/providers/HoveredTableFieldProvider'
import SelectedFieldProvider from '@/components/validation/providers/SelectedFieldProvider'
import DocumentDisplay from './DocumentDisplay'
import DocumentOverlayHeader from './DocumentOverlayHeader'
import { useWorkflowLiteContext } from './idle-components/WorkflowLiteProvider'

const PROCESSING_STATES = [
  'Reading',
  'Extracting',
  'Processing',
  'Cleaning',
  'Cleaning Data',
  'Predicting',
  'Exporting',
  'Sending',
  'Uploading',
  'Building',
  'Confirming',
  'Approving',
  'Verifying',
  'Validating',
]

const hotKeyConfig = {
  preventDefault: true,
  enableOnFormTags: true,
}

type DocumentOverlayProps = {
  documentOverlay: DocumentIdOverlay
  projectId: string
} & ReturnType<typeof useValidationNavList>

const DocumentOverlay = ({
  documentOverlay,
  projectId,
  nextDocumentId,
  ...props
}: DocumentOverlayProps) => {
  const [, setSelectedProjectId] = useSessionStorage(
    'lite-project-id',
    projectId,
  )
  const { workflow } = useWorkflowLiteContext()
  const { overlay, documentIdState, closeDocument } = documentOverlay
  const documentId = documentIdState.value

  const dataQueries = useGetValidationDocDataQueries({
    documentId,
    onlyBasicData: true,
  })

  const { document, refetch } = dataQueries.documentQuery
  const { refetch: refetchRowValues } = dataQueries.documentRowValuesQuery
  const isProcessing = PROCESSING_STATES.includes(
    workflow?.workflow_states?.find(({ id }) =>
      document?.workflow_states_ids?.includes(id),
    )?.name || '',
  )

  // This pre-fetches the next document and puts it into cache so it'll load immediately when the user switches to it
  const { documentQuery: nextDocumentQuery } = useGetValidationDocDataQueries({
    documentId: nextDocumentId || '',
    onlyBasicData: true,
    enabled: !!nextDocumentId,
  })

  const { document: nextDocument } = nextDocumentQuery

  // This pre-fetches the next document's image so it will load from cache
  useEffect(() => {
    if (!nextDocument || !overlay.isOpen) return
    const img = new Image()
    img.src = nextDocument?.document_pages?.[0]?.image_urls.full
  }, [nextDocument, overlay.isOpen])

  // If the project ID of document doesn't match the one in local storage, update it
  useEffect(() => {
    if (document?.project_id && document.project_id !== projectId) {
      setSelectedProjectId(document.project_id)
    }
  }, [document?.project_id, projectId, setSelectedProjectId])

  // Refetch the document if there are document state changes that include the current document
  useNotifications({
    keys: [document?.id],
    callback: ({ action }) => {
      if (document?.id) {
        refetch()
        if (
          action === 'document_workflow_state_status_changed' ||
          action === 'document_workflow_state_changed'
        ) {
          refetchRowValues()
        }
      }
    },
  })

  const {
    hasNextDocument,
    goToPreviousDocument,
    goToNextAvailableDocumentOrClose,
  } = props

  useHotkeys(
    'alt+1',
    () => {
      goToPreviousDocument()
    },
    {
      ...hotKeyConfig,
      enabled: overlay.isOpen,
    },
    [goToPreviousDocument],
  )

  useHotkeys(
    'alt+2',
    () => {
      if (hasNextDocument) {
        goToNextAvailableDocumentOrClose()
      }
    },
    {
      ...hotKeyConfig,
      enabled: overlay.isOpen,
    },
    [hasNextDocument, goToNextAvailableDocumentOrClose],
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog {...overlay} fullScreen onClose={closeDocument}>
        <DocumentOverlayHeader
          documentQuery={dataQueries.documentQuery}
          documentView={isProcessing ? 'processing' : 'idle'}
          onClose={closeDocument}
          {...props}
        />
        <Divider />
        {document ? (
          <SizeProvider>
            <QueryKeysMapProvider>
              <DocumentsChangeSetsProvider>
                <DocumentProvider query={dataQueries.documentQuery} isLite>
                  <DocumentRowsProvider query={dataQueries.documentRowsQuery}>
                    <DocumentRowValuesProvider
                      query={dataQueries.documentRowValuesQuery}
                    >
                      <FieldErrorProvider>
                        <DocumentChipsProvider
                          documentId={documentId}
                          query={dataQueries.documentChipsQuery}
                        >
                          <SelectedFieldProvider>
                            <HoveredTableFieldProvider>
                              <ReactFlowProvider>
                                <DocumentImagePageProvider document={document}>
                                  <DocumentDisplay
                                    document={document}
                                    dataQueries={dataQueries}
                                    isProcessing={isProcessing}
                                  />
                                </DocumentImagePageProvider>
                              </ReactFlowProvider>
                            </HoveredTableFieldProvider>
                          </SelectedFieldProvider>
                        </DocumentChipsProvider>
                      </FieldErrorProvider>
                    </DocumentRowValuesProvider>
                  </DocumentRowsProvider>
                </DocumentProvider>
              </DocumentsChangeSetsProvider>
            </QueryKeysMapProvider>
          </SizeProvider>
        ) : (
          <Stack
            sx={{ height: '100%' }}
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress size={100} />
          </Stack>
        )}
      </Dialog>
    </LocalizationProvider>
  )
}

export default memo(DocumentOverlay)
